import * as React from "react";
import * as _ from "lodash";
import { PickList } from "@bizform/primereact/components/picklist/PickList";
import { PickListProps } from "@bizform/primereact/picklist";
import { Static } from "@bizform/type/decorator/Static.decorator";
import {
    BaseStateModel,
    ComponentElement,
    ComponentElementDisplayMode,
    ComponentElementStatic
} from "@bizform/type/model/component_element/ComponentElement.model";
import {
    ComponentCategory,
    ComponentElementManifest,
    ComponentElementProperty
} from "@bizform/type/model/component_element/ComponentElementManifest.model";
import { DataType } from "@bizform/type/model/model/DataType.model";
import { LookUp } from "@bizform/type/model/data_type/LookUp.model";
import { ComponentHelper, Debug, FunctionHelper, ObjectHelper } from "@bizform/helper/dist/js/bizflow/helper.min";
import { ComponentElementClass, EventResize, ReactComponentElementInit } from "@bizform/core/dist/js/bizflow/core.min";
import { AbstractComponentElement } from "../AbstractComponentElement";
import { I18n } from "../../misc/model/I18n.model";


type Props = PickListProps & {
};

type WrappedCompState = {
    // attribute
    id?: string;
    tabindex?: number;
    source?: any[];
    sourceLookup?: any[];
    target?: any[];
    targetLookup?: any[];
    sourceHeader?: any;
    targetHeader?: any;
    style?: object;
    className?: string;
    printStyle?: object;
    printClassName?: string;
    sourceStyle?: object;
    targetStyle?: object;
    responsive?: boolean;
    showSourceControls?: boolean;
    showTargetControls?: boolean;
    metaKeySelection?: boolean;
    tabIndex?: string;
    lookupDataArray?: Array<any>;
    itemTemplate?(item: any): JSX.Element | undefined;

    // event
    onApply?(e: MouseEvent): void;
    onChange?(e: {event: Event, source: any, target: any}): void;
    onMoveToSource?(e: {originalEvent: Event, value: any}): void;
    onMoveAllToSource?(e: {originalEvent: Event, value: any}): void;
    onMoveToTarget?(e: {originalEvent: Event, value: any}): void;
    onMoveAllToTarget?(e: {originalEvent: Event, value: any}): void;
};

type WrappedFormState = {
    onApply?: string;
    onChange?: string;
    onMoveToSource?: string;
    onMoveAllToSource?: string;
    onMoveToTarget?: string;
    onMoveAllToTarget?: string;
};

type State = BaseStateModel & {
    wrappedCompState: WrappedCompState;
    wrappedFormState: WrappedFormState;
};

/**
 * @since 0.0.1
 * @author Byungmin Choi
 * @class BfPickListLookupData
 * @description BfPickListLookupData component element
 */
@ComponentElementClass()
@Static<ComponentElementStatic<Array<LookUp>>>()
export class BfPickListLookupData extends AbstractComponentElement<Props, State> {
    /* manifest */
    static readonly componentElementManifest: ComponentElementManifest = {
        uniqueId: "_lJJFOQKOackrvM7yqr4D5AmrL6ub3Bd",
        category: ComponentCategory.MISC,
        label: I18n.data.pickListLookupData.label,
        description: I18n.data.pickListLookupData.description,
        dataType: [DataType.LOOKUP],
        onlyArray: true,
        pathToLimitDataTypeArray: "",
        icon: "",
        property: null
    };

    focus(): void {
    }

    setProperty(property: ComponentElementProperty): void {
        try {
            const newState = ComponentHelper.createStateWithProperties<State, React.FormEvent<HTMLInputElement>>(
                property,
                this);
            this.setState(newState);
        } catch (e) {
            this.emitDidCatch(e);
        }
    }

    emit(value: Array<LookUp>): void {
    }

    emitFormState(value: string): void {
    }

    subscription(value: Array<LookUp>): void {
        if (ComponentHelper.isNull(value)) {
            return;
        } else if ( typeof value === "object") {
            const newState = {
                wrappedCompState: {
                    ...this.state.wrappedCompState,
                }
            };

            const targetTemp = value.map((valLookUp) => {
                const returnValue = newState.wrappedCompState.lookupDataArray.find((item) => {
                    return valLookUp.value === item.value;
                });
                if (returnValue) {
                    return returnValue;
                } else {
                    return null;
                }
            });

            const sourceTemp = newState.wrappedCompState.lookupDataArray.map((lookupData) => {
                const targetHasData = targetTemp.some((targetData) => {
                    return targetData.value === lookupData.value;
                });
                if (targetHasData) {
                    return null;
                } else {
                    return lookupData;
                }
            });

            newState.wrappedCompState.source = _.pull(sourceTemp, null);

            newState.wrappedCompState.target = _.pull(targetTemp, null);

            this.setState(newState);
        } else {
            this.emitSubscriptionReject();
        }
    }

    lookupDataSubscription?(lookup: Array<LookUp>): void {
        if (Array.isArray(lookup)) {
            const newState = {
                wrappedCompState: {
                    ...this.state.wrappedCompState,
                    source: lookup,
                    lookupDataArray: lookup
                }
            };

            this.setState(newState);
        }
    }

    @ReactComponentElementInit(BfPickListLookupData)
    static init(): ComponentElement<Array<LookUp>> {
        return;
    }

    initDone(): void {
    }

    destroy(): void {
        this.destroyDone();
    }

    destroyDone(): void {
    }

    /* for React */
    props: Props;
    state: State;

    private _picklist: any;

    constructor(props?: Props) {
        super(props);

        this.state = {
            show: false,
            displayMode: ComponentElementDisplayMode.INIT,
            wrappedCompState: {
                source: [],
                target: [],
                lookupDataArray: []
            },
            wrappedFormState: {}
        };

        this._onChangeEvent = this._onChangeEvent.bind(this);
        this._setRef = this._setRef.bind(this);
        this._position = this._position.bind(this);
    }

    shouldComponentUpdate(nextProps: Readonly<Props>, nextState: Readonly<State>, nextContext: any): boolean {
        return ObjectHelper.hasDifference(nextState, this.state);
    }

    componentDidUpdate(): void {
        if (this._picklist) {
            if (this.state.wrappedCompState.tabindex !== this.___tabindex) {
                ComponentHelper.tabindex(this.displayMode, Array.from(this.parentDom.querySelectorAll("button")), this.state.wrappedCompState.tabindex);
                this.___tabindex = this.state.wrappedCompState.tabindex;
            }
        }
    }

    componentWillUnmount(): void {
        this._picklist = null;
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
        this.emitDidCatch(error);
    }

    emitDidCatch(error: Error | any): void {
        Debug.console.error(error);
    }

    emitSubscriptionReject(): void {
    }

    private async _onApplyEvent(event): Promise<void> {
        this.emit(this.state.wrappedCompState.target);
        await FunctionHelper.timeout(10);
    }

    private _onChangeEvent(event): void {
        const newState = {
            wrappedCompState: {
                ...this.state.wrappedCompState,
                source: event.source,
                target: event.target
            }
        };
        this.setState(newState);
    }

    private _onMoveToSourceEvent(event): void {
    }

    private _onMoveAllToSourceEvent(event): void {
    }

    private _onMoveToTargetEvent(event): void {
    }

    private _onMoveAllToTargetEvent(event): void {
    }

    render(): React.ReactNode {
        const {
            show,
            displayMode
        } = this.state;

        if (!show) {
            return null;
        } else {
            switch (displayMode) {
                case ComponentElementDisplayMode.EDIT:
                    return this._render4Edit();
                case ComponentElementDisplayMode.READONLY:
                case ComponentElementDisplayMode.DISABLED:
                    return this._render4Disabled();
                case ComponentElementDisplayMode.PRINT:
                    return this._render4Print();
                default:
                    return null;
            }
        }
    }

    private _render(newProps: WrappedCompState, disabled: boolean = false): React.ReactNode {
        delete(newProps["printClassName"]);
        delete(newProps["printStyle"]);
        newProps.tabIndex = String(newProps.tabindex);
        return (
            <React.Fragment>
                <PickList
                    {...newProps}
                    key={this.elementId}
                    id={this.elementId}
                    ref={this._setRef}
                    itemTemplate={this._template}
                />
                <button disabled={disabled} className="p-button p-component p-button-primary p-button-text-only float-right mt-1 mr-2" onClick={this.state.wrappedCompState.onApply as any}>Apply</button>
            </React.Fragment>
        );
    }

    private _template(info) {
        return (
            <div className="p-clearfix">
                <div style={{ fontSize: "12px", float: "right", marginRight: "5px" }}>{info.label}</div>
            </div>
        );
    }

    private _render4Edit(): React.ReactNode {
        const newProps = {
            ...this.state.wrappedCompState
        };

        return this._render(newProps);
    }

    private _render4Disabled(): React.ReactNode {
        const newProps = {
            ...this.state.wrappedCompState,
            disabled: true
        };

        return this._render(newProps, true);
    }

    private _render4Print(): React.ReactNode {
        const { target } = this.state.wrappedCompState;
        const result = Object.keys(target).map((index) => {
            const label  = target[index] && target[index].label ? target[index].label : null;
            return label;
        });
        const lookupDataBroken = result.every((item) => {
            item === null;
        });

        return (
            <div ref={this._setRef} style={this.state.wrappedCompState ? {wordBreak: "break-all", ...this.state.wrappedCompState.printStyle} : null} className={`bf-print-mode ${this.state.wrappedCompState ? this.state.wrappedCompState.printClassName : null}`}>{lookupDataBroken ? null : result.join(",")}</div>
        );
    }

    private _setRef(ref) {
        if (ref) {
            this._picklist = ref;
            this._position();
            this.readyComponent();
        }
    }

    @EventResize()
    private _position(e?: Event) {
        const s = Array.from(this.parentDom.querySelectorAll(".p-picklist-caption")) as Array<HTMLElement>;
        if (s.length === 2) {
            if (Math.abs(s[0].offsetTop - s[1].offsetTop) > 10) {
                const b = Array.from(this.parentDom.querySelectorAll(".p-picklist-buttons-cell")[1].querySelectorAll("button>span.pi")) as Array<HTMLElement>;
                b[0].classList.add("pi-angle-down");
                b[1].classList.add("pi-angle-double-down");
                b[2].classList.add("pi-angle-up");
                b[3].classList.add("pi-angle-double-up");
                b[0].classList.remove("pi-angle-right");
                b[1].classList.remove("pi-angle-double-right");
                b[2].classList.remove("pi-angle-left");
                b[3].classList.remove("pi-angle-double-left");
            } else {
                const b = Array.from(this.parentDom.querySelectorAll(".p-picklist-buttons-cell")[1].querySelectorAll("button>span.pi")) as Array<HTMLElement>;
                b[0].classList.remove("pi-angle-down");
                b[1].classList.remove("pi-angle-double-down");
                b[2].classList.remove("pi-angle-up");
                b[3].classList.remove("pi-angle-double-up");
                b[0].classList.add("pi-angle-right");
                b[1].classList.add("pi-angle-double-right");
                b[2].classList.add("pi-angle-left");
                b[3].classList.add("pi-angle-double-left");
            }
        }
    }
}
